import { openComebackDb } from '$infra/db';

/**
 * Non-authoritative local convenience state only. It is intentionally not part
 * of the event log, export, or sync protocol.
 */
export interface ScratchStore {
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  /**
   * Claim a key exactly once. Returns true for the caller that won.
   *
   * Atomic, because IndexedDB's `add` rejects a key that already exists inside
   * the transaction rather than after it. Two tabs opening at the same moment —
   * or two mounts of the same component before a refresh lands — otherwise both
   * read "not yet done", both act, and the log ends up with two records of a
   * thing that is only allowed to happen once (spec §14).
   */
  claim(key: string): Promise<boolean>;
}

export function createIdbScratchStore(): ScratchStore {
  return {
    async get<T>(key: string): Promise<T | undefined> {
      const database = await openComebackDb();
      return (await database.get('scratch', key)) as T | undefined;
    },
    async set<T>(key: string, value: T): Promise<void> {
      const database = await openComebackDb();
      await database.put('scratch', value, key);
    },
    async delete(key: string): Promise<void> {
      const database = await openComebackDb();
      await database.delete('scratch', key);
    },
    async clear(): Promise<void> {
      const database = await openComebackDb();
      await database.clear('scratch');
    },
    async claim(key: string): Promise<boolean> {
      const database = await openComebackDb();
      try {
        await database.add('scratch', Date.now(), key);
        return true;
      } catch (error) {
        if (typeof error === 'object' && error !== null && 'name' in error
          && error.name === 'ConstraintError') {
          return false;
        }
        throw error;
      }
    },
  };
}
